import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "CobrosSuscripciones";

const RUTAS_FRONTEND = {
  inicio: "/inicio",
  nuevo: "/cobros/renovaciones/nueva",
  editar: (id) => `/cobros/renovaciones/edit/${id}`,
};

/* =========================================================
   UTILIDADES DE SESIÓN Y PERMISOS
========================================================= */

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const leerStorageJSON = (clave) => {
  try {
    const valor = localStorage.getItem(clave);
    return valor ? JSON.parse(valor) : null;
  } catch (error) {
    console.error(`No se pudo leer ${clave}:`, error);
    return null;
  }
};

const obtenerUsuarioStorage = () =>
  leerStorageJSON("usuario") ||
  leerStorageJSON("user") ||
  leerStorageJSON("usuarios") ||
  {};

const obtenerRolStorage = () => {
  const permisos = leerStorageJSON("permisos") || {};
  const usuario = obtenerUsuarioStorage();

  return (
    permisos?.rolObjectId?.nombreRol ||
    permisos?.rol?.nombreRol ||
    permisos?.rol?.nombre ||
    permisos?.rol ||
    permisos?.nombreRol ||
    usuario?.rolObjectId?.nombreRol ||
    usuario?.rol?.nombreRol ||
    usuario?.rol?.nombre ||
    usuario?.rol ||
    usuario?.role ||
    usuario?.tipoUsuario ||
    usuario?.nombreRol ||
    usuario?.usuario?.rolObjectId?.nombreRol ||
    usuario?.usuario?.rol?.nombreRol ||
    usuario?.usuario?.rol?.nombre ||
    usuario?.usuario?.rol ||
    usuario?.user?.rolObjectId?.nombreRol ||
    usuario?.user?.rol?.nombreRol ||
    usuario?.user?.rol?.nombre ||
    usuario?.user?.rol ||
    ""
  );
};

const esSuperAdmin = () =>
  normalizarTexto(obtenerRolStorage()) === "superadmin";

const obtenerModulosStorage = () => {
  const permisos = leerStorageJSON("permisos");
  const usuario = obtenerUsuarioStorage();

  const modulos = Array.isArray(permisos)
    ? permisos
    : permisos?.modulos ||
      permisos?.permisos ||
      usuario?.modulos ||
      usuario?.permisos ||
      usuario?.rol?.modulos ||
      usuario?.rol?.permisos ||
      usuario?.usuario?.modulos ||
      usuario?.usuario?.permisos ||
      usuario?.user?.modulos ||
      usuario?.user?.permisos ||
      [];

  return Array.isArray(modulos) ? modulos : [];
};

const obtenerModuloPorNombre = (nombreModulo) => {
  const moduloBuscado = normalizarTexto(nombreModulo);

  return (
    obtenerModulosStorage().find((modulo) => {
      const nombre =
        modulo?.nombreModulo ||
        modulo?.modulo ||
        modulo?.moduloNombre ||
        modulo?.nombre ||
        modulo?.name ||
        modulo?.moduloObjectId?.nombreModulo ||
        modulo?.moduloObjectId?.nombre ||
        "";

      return normalizarTexto(nombre) === moduloBuscado;
    }) || {}
  );
};

const tienePermiso = (modulo, accion) => {
  /*
   * REGLA DEFINITIVA:
   * SOLO SUPER_ADMIN obtiene todos los permisos automáticamente.
   * Los demás roles dependen de la matriz de permisos.
   */
  if (esSuperAdmin()) {
    return true;
  }

  const accionNormalizada = normalizarTexto(accion);

  const camposPorAccion = {
    leer: [
      "puedeLeer",
      "leer",
      "read",
      "ver",
      "listar",
    ],
    crear: [
      "puedeCrear",
      "crear",
      "create",
      "add",
      "registrar",
    ],
    actualizar: [
      "puedeActualizar",
      "actualizar",
      "update",
      "editar",
      "edit",
    ],
    eliminar: [
      "puedeEliminar",
      "eliminar",
      "delete",
      "borrar",
      "remove",
    ],
  };

  const campos =
    camposPorAccion[accionNormalizada] || [accion];

  const permisoDirecto = campos.some(
    (campo) =>
      modulo?.[campo] === true ||
      modulo?.acciones?.[campo] === true ||
      modulo?.permisos?.[campo] === true
  );

  const permisosEnArreglo = [
    ...(Array.isArray(modulo?.acciones)
      ? modulo.acciones
      : []),
    ...(Array.isArray(modulo?.permisos)
      ? modulo.permisos
      : []),
    ...(Array.isArray(modulo?.operaciones)
      ? modulo.operaciones
      : []),
  ].map(normalizarTexto);

  return (
    permisoDirecto ||
    campos.some((campo) =>
      permisosEnArreglo.includes(
        normalizarTexto(campo)
      )
    )
  );
};

/* =========================================================
   UTILIDADES VISUALES
========================================================= */

const formatearFecha = (fecha) => {
  if (!fecha) return "—";

  const date = new Date(fecha);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const formatearMoneda = (
  valor,
  moneda = "COP"
) => {
  const numero = Number(valor || 0);

  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: moneda || "COP",
      maximumFractionDigits: 0,
    }).format(numero);
  } catch {
    return `$ ${numero.toLocaleString("es-CO")}`;
  }
};

const nombreInstructor = (instructor) => {
  if (!instructor) return "—";

  if (typeof instructor === "string") {
    return instructor;
  }

  return (
    [
      instructor.nombre1Instructor,
      instructor.nombre2Instructor,
      instructor.apellido1Instructor,
      instructor.apellido2Instructor,
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim() || "—"
  );
};

const badgeEstado = (estado = "") => {
  const valor = String(
    estado || ""
  ).toUpperCase();

  const clases = {
    APROBADA: "text-bg-success",
    APROBADO: "text-bg-success",

    PENDIENTE: "text-bg-warning",
    EN_REVISION: "text-bg-info",

    RECHAZADA: "text-bg-danger",
    RECHAZADO: "text-bg-danger",

    CANCELADA: "text-bg-secondary",
    ANULADO: "text-bg-secondary",
    REEMBOLSADO: "text-bg-secondary",
  };

  const clase =
    clases[valor] ||
    "text-bg-secondary";

  return (
    <span
      className={`badge rounded-pill ${clase}`}
    >
      {valor.replaceAll("_", " ") || "—"}
    </span>
  );
};

const escaparHtml = (valor = "") =>
  String(valor)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

/* =========================================================
   COMPONENTE
========================================================= */

const ListarSolicitudesRenovacion = () => {
  const navigate = useNavigate();

  const [
    solicitudes,
    setSolicitudes,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    procesandoId,
    setProcesandoId,
  ] = useState(null);

  /* =======================================================
     AUTENTICACIÓN
  ======================================================= */

  const authConfig = useCallback(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);

  /* =======================================================
     PERMISOS
  ======================================================= */

  const moduloCobros = useMemo(
    () =>
      obtenerModuloPorNombre(
        MODULO
      ),
    []
  );

  const puedeLeer = useMemo(
    () =>
      tienePermiso(
        moduloCobros,
        "LEER"
      ),
    [moduloCobros]
  );

  const puedeCrear = useMemo(
    () =>
      tienePermiso(
        moduloCobros,
        "CREAR"
      ),
    [moduloCobros]
  );

  const puedeActualizar = useMemo(
    () =>
      tienePermiso(
        moduloCobros,
        "ACTUALIZAR"
      ),
    [moduloCobros]
  );

  const puedeEliminar = useMemo(
    () =>
      tienePermiso(
        moduloCobros,
        "ELIMINAR"
      ),
    [moduloCobros]
  );

  /* =======================================================
     SESIÓN
  ======================================================= */

  const manejarErrorSesion = useCallback(
    (error) => {
      if (
        error?.response?.status === 401
      ) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text:
            "Por favor inicia sesión nuevamente.",
          confirmButtonText:
            "Aceptar",
        }).then(() => {
          localStorage.removeItem(
            "token"
          );

          sessionStorage.removeItem(
            "token"
          );

          navigate("/", {
            replace: true,
          });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  /* =======================================================
     LISTAR
  ======================================================= */

  const getData = useCallback(
    async () => {
      if (!puedeLeer) {
        setSolicitudes([]);
        return;
      }

      try {
        setLoading(true);

        const { data } =
          await axios.get(
            "/api/renovaciones/listall",
            authConfig()
          );

        if (
          !data ||
          !Array.isArray(
            data.solicitudes
          )
        ) {
          throw new Error(
            "Datos de solicitudes no válidos."
          );
        }

        setSolicitudes(
          data.solicitudes
        );
      } catch (error) {
        console.error(
          "Error al cargar solicitudes de renovación:",
          error
        );

        if (
          manejarErrorSesion(
            error
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudieron cargar las solicitudes.",
          "error"
        );

        setSolicitudes([]);
      } finally {
        setLoading(false);
      }
    },
    [
      authConfig,
      manejarErrorSesion,
      puedeLeer,
    ]
  );

  useEffect(() => {
    getData();
  }, [getData]);

  /* =======================================================
     VER DETALLE
  ======================================================= */

  const verDetalle = useCallback(
    (solicitud) => {
      const instructor =
        solicitud.instructorObjectId;

      const plan =
        solicitud.planSolicitadoObjectId;

      const pago =
        solicitud.pagoObjectId;

      const estado =
        String(
          solicitud.estadoSolicitud ||
            ""
        ).toUpperCase();

      const pagoEstado =
        pago?.estadoPago
          ? String(
              pago.estadoPago
            ).replaceAll(
              "_",
              " "
            )
          : "SIN PAGO";

      const pagoValor =
        pago?.valorPago !==
          undefined &&
        pago?.valorPago !== null
          ? formatearMoneda(
              pago.valorPago,
              pago.moneda
            )
          : "—";

      Swal.fire({
        title:
          "Detalle de la renovación",
        width: 680,
        confirmButtonText: "Cerrar",
        html: `
          <div class="text-start">
            <div class="border rounded-3 p-3 bg-light">
              <div class="row g-3">

                <div class="col-12">
                  <div class="small text-secondary text-uppercase">
                    Instructor
                  </div>
                  <div class="fw-semibold">
                    ${escaparHtml(
                      nombreInstructor(
                        instructor
                      )
                    )}
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="small text-secondary text-uppercase">
                    Documento
                  </div>
                  <div class="fw-semibold">
                    ${escaparHtml(
                      instructor
                        ?.documentoInstructor ||
                        "—"
                    )}
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="small text-secondary text-uppercase">
                    Plan solicitado
                  </div>
                  <div class="fw-semibold">
                    ${escaparHtml(
                      plan?.nombrePlan ||
                        "—"
                    )}
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="small text-secondary text-uppercase">
                    Fecha de solicitud
                  </div>
                  <div class="fw-semibold">
                    ${escaparHtml(
                      formatearFecha(
                        solicitud.fechaSolicitud
                      )
                    )}
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="small text-secondary text-uppercase">
                    Estado de solicitud
                  </div>
                  <div class="fw-semibold">
                    ${escaparHtml(
                      estado.replaceAll(
                        "_",
                        " "
                      ) || "—"
                    )}
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="small text-secondary text-uppercase">
                    Estado del pago
                  </div>
                  <div class="fw-semibold">
                    ${escaparHtml(
                      pagoEstado
                    )}
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="small text-secondary text-uppercase">
                    Valor del pago
                  </div>
                  <div class="fw-semibold">
                    ${escaparHtml(
                      pagoValor
                    )}
                  </div>
                </div>

                ${
                  solicitud.observacionAdministrador
                    ? `
                      <div class="col-12">
                        <div class="small text-secondary text-uppercase">
                          Observación
                        </div>
                        <div>
                          ${escaparHtml(
                            solicitud.observacionAdministrador
                          )}
                        </div>
                      </div>
                    `
                    : ""
                }

                ${
                  solicitud.motivoRechazo
                    ? `
                      <div class="col-12">
                        <div class="small text-secondary text-uppercase">
                          Motivo de rechazo
                        </div>
                        <div>
                          ${escaparHtml(
                            solicitud.motivoRechazo
                          )}
                        </div>
                      </div>
                    `
                    : ""
                }

              </div>
            </div>
          </div>
        `,
      });
    },
    []
  );

  /* =======================================================
     ENVIAR A REVISIÓN
  ======================================================= */

  const enviarRevision = useCallback(
    async (solicitud) => {
      if (!puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permiso ACTUALIZAR para revisar solicitudes.",
          "warning"
        );
        return;
      }

      const estado =
        String(
          solicitud.estadoSolicitud ||
            ""
        ).toUpperCase();

      if (
        estado !== "PENDIENTE"
      ) {
        Swal.fire(
          "Operación no permitida",
          "Solo una solicitud PENDIENTE puede enviarse a revisión.",
          "warning"
        );
        return;
      }

      const result =
        await Swal.fire({
          title:
            "Enviar a revisión",
          input: "textarea",
          inputLabel:
            "Observación (opcional)",
          inputPlaceholder:
            "Escriba una observación...",
          showCancelButton: true,
          confirmButtonText:
            "Enviar a revisión",
          cancelButtonText:
            "Cancelar",
        });

      if (!result.isConfirmed) {
        return;
      }

      try {
        setProcesandoId(
          solicitud._id
        );

        const { data } =
          await axios.put(
            `/api/renovaciones/enrevision/${solicitud._id}`,
            {
              observacionAdministrador:
                result.value || "",
            },
            authConfig()
          );

        await Swal.fire({
          icon: "success",
          title:
            data?.message ||
            "Solicitud enviada a revisión",
          showConfirmButton: false,
          timer: 1600,
        });

        await getData();
      } catch (error) {
        console.error(
          "Error al enviar solicitud a revisión:",
          error
        );

        if (
          manejarErrorSesion(
            error
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudo actualizar la solicitud.",
          "error"
        );
      } finally {
        setProcesandoId(null);
      }
    },
    [
      authConfig,
      getData,
      manejarErrorSesion,
      puedeActualizar,
    ]
  );

  /* =======================================================
     APROBAR
  ======================================================= */

  const aprobarSolicitud = useCallback(
    async (solicitud) => {
      if (!puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permiso ACTUALIZAR para aprobar solicitudes.",
          "warning"
        );
        return;
      }

      const estado =
        String(
          solicitud.estadoSolicitud ||
            ""
        ).toUpperCase();

      if (
        ![
          "PENDIENTE",
          "EN_REVISION",
        ].includes(estado)
      ) {
        Swal.fire(
          "Operación no permitida",
          "La solicitud no puede aprobarse en su estado actual.",
          "warning"
        );
        return;
      }

      const pago =
        solicitud.pagoObjectId;

      if (
        !pago ||
        String(
          pago.estadoPago ||
            ""
        ).toUpperCase() !==
          "APROBADO"
      ) {
        Swal.fire({
          icon: "warning",
          title:
            "Pago pendiente de aprobación",
          text:
            "La solicitud de renovación no puede aprobarse hasta que el pago asociado esté APROBADO.",
          confirmButtonText:
            "Entendido",
        });

        return;
      }

      const result =
        await Swal.fire({
          title:
            "Aprobar renovación",
          input: "textarea",
          inputLabel:
            "Observación (opcional)",
          inputPlaceholder:
            "Escriba una observación...",
          icon: "question",
          showCancelButton: true,
          confirmButtonText:
            "Aprobar",
          cancelButtonText:
            "Cancelar",
        });

      if (!result.isConfirmed) {
        return;
      }

      try {
        setProcesandoId(
          solicitud._id
        );

        const { data } =
          await axios.put(
            `/api/renovaciones/aprobar/${solicitud._id}`,
            {
              observacionAdministrador:
                result.value || "",
            },
            authConfig()
          );

        await Swal.fire({
          icon: "success",
          title:
            data?.message ||
            "Solicitud aprobada",
        });

        await getData();
      } catch (error) {
        console.error(
          "Error al aprobar solicitud:",
          error
        );

        if (
          manejarErrorSesion(
            error
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudo aprobar la solicitud.",
          "error"
        );
      } finally {
        setProcesandoId(null);
      }
    },
    [
      authConfig,
      getData,
      manejarErrorSesion,
      puedeActualizar,
    ]
  );

  /* =======================================================
     RECHAZAR
  ======================================================= */

  const rechazarSolicitud = useCallback(
    async (solicitud) => {
      if (!puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permiso ACTUALIZAR para rechazar solicitudes.",
          "warning"
        );
        return;
      }

      const estado =
        String(
          solicitud.estadoSolicitud ||
            ""
        ).toUpperCase();

      if (
        ![
          "PENDIENTE",
          "EN_REVISION",
        ].includes(estado)
      ) {
        Swal.fire(
          "Operación no permitida",
          "La solicitud no puede rechazarse en su estado actual.",
          "warning"
        );
        return;
      }

      const result =
        await Swal.fire({
          title:
            "Rechazar renovación",
          input: "textarea",
          inputLabel:
            "Motivo del rechazo",
          inputPlaceholder:
            "Escriba el motivo...",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText:
            "Rechazar",
          cancelButtonText:
            "Cancelar",
          inputValidator: (
            value
          ) =>
            !String(
              value || ""
            ).trim()
              ? "Indique el motivo del rechazo."
              : undefined,
        });

      if (!result.isConfirmed) {
        return;
      }

      try {
        setProcesandoId(
          solicitud._id
        );

        const { data } =
          await axios.put(
            `/api/renovaciones/rechazar/${solicitud._id}`,
            {
              motivoRechazo:
                String(
                  result.value ||
                    ""
                ).trim(),
            },
            authConfig()
          );

        await Swal.fire({
          icon: "success",
          title:
            data?.message ||
            "Solicitud rechazada",
          showConfirmButton: false,
          timer: 1700,
        });

        await getData();
      } catch (error) {
        console.error(
          "Error al rechazar solicitud:",
          error
        );

        if (
          manejarErrorSesion(
            error
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudo rechazar la solicitud.",
          "error"
        );
      } finally {
        setProcesandoId(null);
      }
    },
    [
      authConfig,
      getData,
      manejarErrorSesion,
      puedeActualizar,
    ]
  );

  /* =======================================================
     CANCELAR
  ======================================================= */

  const cancelarSolicitud = useCallback(
    async (solicitud) => {
      if (!puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permiso ACTUALIZAR para cancelar solicitudes.",
          "warning"
        );
        return;
      }

      const estado =
        String(
          solicitud.estadoSolicitud ||
            ""
        ).toUpperCase();

      if (
        ![
          "PENDIENTE",
          "EN_REVISION",
        ].includes(estado)
      ) {
        Swal.fire(
          "Operación no permitida",
          "La solicitud no puede cancelarse en su estado actual.",
          "warning"
        );
        return;
      }

      const result =
        await Swal.fire({
          title:
            "¿Cancelar solicitud?",
          text:
            "La solicitud quedará en estado CANCELADA.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText:
            "Sí, cancelar",
          cancelButtonText:
            "Volver",
        });

      if (!result.isConfirmed) {
        return;
      }

      try {
        setProcesandoId(
          solicitud._id
        );

        const { data } =
          await axios.put(
            `/api/renovaciones/cancelar/${solicitud._id}`,
            {},
            authConfig()
          );

        await Swal.fire({
          icon: "success",
          title:
            data?.message ||
            "Solicitud cancelada",
          showConfirmButton: false,
          timer: 1600,
        });

        await getData();
      } catch (error) {
        console.error(
          "Error al cancelar solicitud:",
          error
        );

        if (
          manejarErrorSesion(
            error
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudo cancelar la solicitud.",
          "error"
        );
      } finally {
        setProcesandoId(null);
      }
    },
    [
      authConfig,
      getData,
      manejarErrorSesion,
      puedeActualizar,
    ]
  );

  /* =======================================================
     ELIMINAR
  ======================================================= */

  const eliminarSolicitud = useCallback(
    async (solicitud) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permiso ELIMINAR para borrar solicitudes.",
          "warning"
        );
        return;
      }

      const estado =
        String(
          solicitud.estadoSolicitud ||
            ""
        ).toUpperCase();

      if (
        ![
          "RECHAZADA",
          "CANCELADA",
        ].includes(estado)
      ) {
        Swal.fire(
          "Operación no permitida",
          "Solo pueden eliminarse solicitudes rechazadas o canceladas.",
          "warning"
        );
        return;
      }

      const result =
        await Swal.fire({
          title:
            "¿Eliminar solicitud?",
          text:
            "Esta acción eliminará el registro de la solicitud.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText:
            "Sí, eliminar",
          cancelButtonText:
            "Cancelar",
          confirmButtonColor:
            "#dc3545",
        });

      if (!result.isConfirmed) {
        return;
      }

      try {
        setProcesandoId(
          solicitud._id
        );

        const { data } =
          await axios.delete(
            `/api/renovaciones/delete/${solicitud._id}`,
            authConfig()
          );

        await Swal.fire({
          icon: "success",
          title:
            data?.message ||
            "Solicitud eliminada",
          showConfirmButton: false,
          timer: 1600,
        });

        await getData();
      } catch (error) {
        console.error(
          "Error al eliminar solicitud:",
          error
        );

        if (
          manejarErrorSesion(
            error
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudo eliminar la solicitud.",
          "error"
        );
      } finally {
        setProcesandoId(null);
      }
    },
    [
      authConfig,
      getData,
      manejarErrorSesion,
      puedeEliminar,
    ]
  );

  /* =======================================================
     GESTIONAR SOLICITUD

     Un solo botón concentra las decisiones:
     - pasar a revisión
     - aprobar
     - rechazar
     - cancelar
  ======================================================= */

  const gestionarSolicitud = useCallback(
    async (solicitud) => {
      if (!puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permiso ACTUALIZAR para gestionar solicitudes.",
          "warning"
        );
        return;
      }

      const estado =
        String(
          solicitud.estadoSolicitud ||
            ""
        ).toUpperCase();

      if (
        ![
          "PENDIENTE",
          "EN_REVISION",
        ].includes(estado)
      ) {
        Swal.fire(
          "Sin acciones disponibles",
          "La solicitud ya no se encuentra en un estado gestionable.",
          "info"
        );
        return;
      }

      const opciones =
        estado === "PENDIENTE"
          ? {
              EN_REVISION:
                "Enviar a revisión",
              APROBAR:
                "Aprobar renovación",
              RECHAZAR:
                "Rechazar renovación",
              CANCELAR:
                "Cancelar solicitud",
            }
          : {
              APROBAR:
                "Aprobar renovación",
              RECHAZAR:
                "Rechazar renovación",
              CANCELAR:
                "Cancelar solicitud",
            };

      const { value: accion } =
        await Swal.fire({
          title:
            "Gestionar renovación",
          input: "select",
          inputLabel:
            `Estado actual: ${estado.replaceAll(
              "_",
              " "
            )}`,
          inputOptions: opciones,
          inputPlaceholder:
            "Seleccione una acción",
          showCancelButton: true,
          confirmButtonText:
            "Continuar",
          cancelButtonText:
            "Cerrar",
          inputValidator: (
            value
          ) =>
            !value
              ? "Seleccione una acción."
              : undefined,
        });

      if (!accion) {
        return;
      }

      switch (accion) {
        case "EN_REVISION":
          await enviarRevision(
            solicitud
          );
          break;

        case "APROBAR":
          await aprobarSolicitud(
            solicitud
          );
          break;

        case "RECHAZAR":
          await rechazarSolicitud(
            solicitud
          );
          break;

        case "CANCELAR":
          await cancelarSolicitud(
            solicitud
          );
          break;

        default:
          break;
      }
    },
    [
      aprobarSolicitud,
      cancelarSolicitud,
      enviarRevision,
      puedeActualizar,
      rechazarSolicitud,
    ]
  );

  /* =======================================================
     RESUMEN
  ======================================================= */

  const resumen = useMemo(() => {
    const contar = (
      estado
    ) =>
      solicitudes.filter(
        (solicitud) =>
          String(
            solicitud.estadoSolicitud ||
              ""
          ).toUpperCase() ===
          estado
      ).length;

    return {
      total: solicitudes.length,
      pendientes:
        contar("PENDIENTE"),
      revision:
        contar("EN_REVISION"),
      aprobadas:
        contar("APROBADA"),
    };
  }, [solicitudes]);

  /* =======================================================
     TABLA
  ======================================================= */

  const dataTabla = useMemo(
    () =>
      solicitudes.map(
        (solicitud) => {
          const instructor =
            solicitud.instructorObjectId;

          const plan =
            solicitud.planSolicitadoObjectId;

          const pago =
            solicitud.pagoObjectId;

          const estado =
            String(
              solicitud.estadoSolicitud ||
                ""
            ).toUpperCase();

          return {
            ...solicitud,

            instructorNombre:
              nombreInstructor(
                instructor
              ),

            documentoInstructor:
              instructor
                ?.documentoInstructor ||
              "—",

            planNombre:
              plan?.nombrePlan ||
              "—",

            fechaSolicitudFormateada:
              formatearFecha(
                solicitud.fechaSolicitud
              ),

            pagoEstado:
              pago?.estadoPago
                ? badgeEstado(
                    pago.estadoPago
                  )
                : (
                    <span className="badge rounded-pill text-bg-light border text-dark">
                      Sin pago
                    </span>
                  ),

            estadoVisual:
              badgeEstado(
                estado
              ),

            funciones: (
              <div className="d-flex align-items-center gap-2 flex-nowrap">
                {/* DETALLE */}
                <button
                  type="button"
                  className="btn btn-light border btn-sm px-2 d-inline-flex align-items-center gap-1"
                  onClick={() =>
                    verDetalle(
                      solicitud
                    )
                  }
                  title="Ver detalle"
                  disabled={
                    loading ||
                    procesandoId ===
                      solicitud._id
                  }
                >
                  <i className="fa-solid fa-eye"></i>

                  <span className="d-none d-xxl-inline">
                    Detalle
                  </span>
                </button>

                {/* EDITAR */}
                {puedeActualizar &&
                  estado ===
                    "PENDIENTE" && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm px-2 d-inline-flex align-items-center gap-1"
                      onClick={() =>
                        navigate(
                          RUTAS_FRONTEND.editar(
                            solicitud._id
                          )
                        )
                      }
                      title="Editar solicitud"
                      disabled={
                        loading ||
                        procesandoId ===
                          solicitud._id
                      }
                    >
                      <i className="fa-solid fa-pen"></i>

                      <span className="d-none d-xxl-inline">
                        Editar
                      </span>
                    </button>
                  )}

                {/* GESTIONAR */}
                {puedeActualizar &&
                  [
                    "PENDIENTE",
                    "EN_REVISION",
                  ].includes(
                    estado
                  ) && (
                    <button
                      type="button"
                      className="btn btn-dark btn-sm px-3 d-inline-flex align-items-center gap-2"
                      onClick={() =>
                        gestionarSolicitud(
                          solicitud
                        )
                      }
                      title="Gestionar renovación"
                      disabled={
                        loading ||
                        procesandoId ===
                          solicitud._id
                      }
                    >
                      <i className="fa-solid fa-list-check"></i>
                      <span>
                        Gestionar
                      </span>
                    </button>
                  )}

                {/* ELIMINAR SOLO ESTADOS TERMINALES */}
                {puedeEliminar &&
                  [
                    "RECHAZADA",
                    "CANCELADA",
                  ].includes(
                    estado
                  ) && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm px-2 d-inline-flex align-items-center justify-content-center"
                      onClick={() =>
                        eliminarSolicitud(
                          solicitud
                        )
                      }
                      title="Eliminar solicitud"
                      disabled={
                        loading ||
                        procesandoId ===
                          solicitud._id
                      }
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  )}
              </div>
            ),
          };
        }
      ),
    [
      solicitudes,
      loading,
      procesandoId,
      puedeActualizar,
      puedeEliminar,
      navigate,
      verDetalle,
      gestionarSolicitud,
      eliminarSolicitud,
    ]
  );

  const columns = useMemo(
    () => [
      {
        Header:
          "Documento",
        accessor:
          "documentoInstructor",
      },
      {
        Header:
          "Instructor",
        accessor:
          "instructorNombre",
      },
      {
        Header:
          "Plan solicitado",
        accessor:
          "planNombre",
      },
      {
        Header: "Fecha",
        accessor:
          "fechaSolicitudFormateada",
      },
      {
        Header: "Pago",
        accessor:
          "pagoEstado",
        hideHeaderOnMobile:
          true,
      },
      {
        Header: "Estado",
        accessor:
          "estadoVisual",
      },
      {
        Header: "Acciones",
        accessor:
          "funciones",
      },
    ],
    []
  );

  /* =======================================================
     SIN PERMISO DE LECTURA
  ======================================================= */

  if (!puedeLeer) {
    return (
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-4">
          <div className="alert alert-warning mb-3">
            <i className="fa-solid fa-triangle-exclamation me-2"></i>
            No tiene permisos para consultar solicitudes de renovación.
          </div>

          <button
            type="button"
            className="btn btn-dark"
            onClick={() =>
              navigate(
                RUTAS_FRONTEND.inicio
              )
            }
          >
            <i className="fa-solid fa-house me-2"></i>
            Inicio
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     VISTA
  ======================================================= */

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm">
          {/* ENCABEZADO */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white rounded-top-4">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Solicitudes de renovación
                </h5>

                <small className="text-secondary d-block">
                  Control del proceso de renovación de suscripciones.
                </small>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <span className="badge rounded-pill text-bg-light border text-dark">
                    Total: {resumen.total}
                  </span>

                  <span className="badge rounded-pill text-bg-warning">
                    Pendientes: {resumen.pendientes}
                  </span>

                  <span className="badge rounded-pill text-bg-info">
                    En revisión: {resumen.revision}
                  </span>

                  <span className="badge rounded-pill text-bg-success">
                    Aprobadas: {resumen.aprobadas}
                  </span>
                </div>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() =>
                    navigate(
                      RUTAS_FRONTEND.inicio
                    )
                  }
                  disabled={loading}
                >
                  <i className="fa-solid fa-house me-2"></i>
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(
                        RUTAS_FRONTEND.nuevo
                      )
                    }
                    disabled={loading}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nueva solicitud
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* TABLA */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">
                  Cargando solicitudes…
                </div>
              </div>
            ) : dataTabla.length >
              0 ? (
              <div className="overflow-auto">
                <DataTable
                  columns={columns}
                  data={dataTabla}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5 px-3">
                <div className="display-6 mb-2">
                  🔄
                </div>

                <div className="fw-semibold">
                  No hay solicitudes de renovación
                </div>

                <div className="small mb-3">
                  Cuando se registre una solicitud de renovación aparecerá aquí.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(
                        RUTAS_FRONTEND.nuevo
                      )
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Solicitar renovación
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarSolicitudesRenovacion;